import {forkJoin, Observable, of} from 'rxjs';
import {Injectable} from '@angular/core';
import {
    AuditId,
    IntegrationId,
    ProjectId,
    QASessionAssociationId,
    QASessionId,
    ReportSpecId,
    SpecGroupId,
    SpecId,
    SpecRef,
    TestExecutionId,
    FindingId,
    FindingTagRuleId,
    StateCollectorJobId,
    TestRunId,
    TestSessionId,
    TestTemplateId, TestObjectCollectionId, TestObjectResolutionJobId, TestObjectId
} from '../../types/ids';
import {AuthService} from '../../services/auth.service';
import {APIResult, ApiSourcedPaginationRequest, GenericApiResponse, QueryMetadata, queryMetadataToQueryParams} from '../apibase.api';
import {TagRule} from '../../types/tests/tag-rule';
import { HttpClient } from '@angular/common/http';
import {isDefined} from '@webmate-web-core/shared/utils/utils';
import {
    AdHocReportSpec,
    AssignmentSpec,
    CreateSpecDTO,
    CreateSpecGroupDTO,
    FilterSpec,
    ContentCell,
    ContentCellId,
    GroupingSpec,
    Spec,
    SpecGroup,
    SpecGroupUpdateAction,
    SpecUpdateAction,
    TestExecutionSummary,
    TestReportSpec,
    Finding,
    FindingCreationData,
    FindingUpdateData,
    TestRun,
    TestRunCreationSpec,
    TestRunReportConfig,
    TestRunEvaluationStatus,
    TestSession,
    TestSessionInfo,
    TestTemplate, FindingsQuerySpec, FindingsQueryResult, FindingInfo, ObservationData,
    CreateTestObjectCollectionDTO,
    CreateTestObjectDTO,
    TestObject,
    TestObjectCollection,
    TestObjectCollectionSummary,
    TestObjectCollectionUpdateAction,
    TestObjectStateSummary,
    TestObjectResolutionJob,
    TestObjectsSourceSpec,
    CreateQASessionAssociationData,
    CreateQASessionData,
    OpenTestObjectInExpeditionRequest,
    QASession,
    QASessionAssociation,
    QASessionAssociationTargetType,
    UpdateQASessionData
} from '@webmate-web-core/shared/types/testmgmt';
import {CreateTestExecutionResult, TestObjectUpdateAction} from '@webmate-web-core/shared/services/test.service';
import {Tag} from '@webmate-web-core/shared/types/tag';
import {IssueSeverity} from '../../console/types/shared';
import {catchError, map, switchMap, take} from 'rxjs/operators';
import {WMValue} from '@webmate-web-core/shared/types/base-types';
import {
    IssueReporterDefaults,
    IssueReporterInsertDTO,
    IssueReporterIntegration,
    IssueReporterProjectWithIssueTypes,
    IssueReportingInfo,
    IssueSourceType,
    IssueReporterParametersResponse
} from '@webmate-web-core/shared/types/integrations';
import {ReportId} from '../../../../report/report_types';
import {BackendSpecForestNode} from '@webmate-web-core/shared/services/specs';
import {AutomationPolicyConfig} from '@webmate-web-core/shared/types/browsersession';
import {TestRunResultProjection} from '@webmate-web-core/shared/types/test-run-result-projection';

export type FindingCellAction = {
    type: 'AppendFindingCell',
    cell: ContentCell
}  | {
    type: 'AppendFindingCells',
    cells: Array<ContentCell>
} | {
    type: 'UpdateFindingCell',
    cell: ContentCell,
    cellId: ContentCellId
} | {
    type: 'InsertFindingCell',
    cell: ContentCell,
    predecessor?: ContentCellId
} | {
    type: 'DeleteFindingCell',
    cellId: ContentCellId
} | {
    type: 'MoveFindingCell',
    cellId: ContentCellId,
    predecessor?: ContentCellId
}

export interface SpecCollectionDTO {
    collectionId: string;
    versionNo: number;
    projectId: ProjectId;
    name: string;
    description?: string;
    specIds: SpecId[];
    isBaseProjectCollection: boolean;
}

@Injectable({
    providedIn: 'root'
})
export class TestApi {

    constructor(public OAuth2AuthService: AuthService, private http: HttpClient) {
    }

    copyTestTemplate(projectId: ProjectId, testId: TestTemplateId, name?: string, description?: string, defaults?: AssignmentSpec): Observable<TestTemplateId> {
        let body: any = {
            testId: testId
        };
        if (isDefined(name) && isDefined(description) && isDefined(defaults)) {
            body['testUpdate'] = {
                name: name,
                description: description,
                models: [], // TODO
                params: defaults
            };
        }
        return this.http.post<TestTemplateId>(`/projects/${projectId}/tests`, body);
    }

    updateTestTemplate(id: TestTemplateId, name?: string, description?: string, defaults?: AssignmentSpec): Observable<GenericApiResponse> {
        return this.http.put<GenericApiResponse>(`/testmgmt/tests/${id}`, {
            name: name,
            description: description,
            models: [], // TODO
            params: defaults
        });
    }

    getTestTemplate(testId: TestTemplateId): Observable<TestTemplate> {
        return this.http.get<TestTemplate>(`/testmgmt/tests/${testId}`);
    }

    getTestTemplatesInProject(projectId: ProjectId): Observable<Array<TestTemplate>> {
        return this.http.get<Array<TestTemplate>>(`/projects/${projectId}/tests`);
    }

    deleteTestTemplate(testId: TestTemplateId): Observable<GenericApiResponse> {
        return this.http.delete<GenericApiResponse>(`/testmgmt/tests/${testId}`);
    }

    overrideTestRunEvaluationStatus(testRunId: TestRunId, status: TestRunEvaluationStatus, message?: string): Observable<GenericApiResponse> {
        const testRunData = {
            status: status,
            message: message,
        };

        return this.http.post<any>(`/testmgmt/testruns/${testRunId}/evaluationstatus`, testRunData);
    }

    finishTestRun(testRunId: TestRunId, status?: TestRunEvaluationStatus, message?: string): Observable<GenericApiResponse> {
        const testRunData = {
            status: status,
            message: message,
        };

        return this.http.post<any>(`/testmgmt/testruns/${testRunId}/finish`, testRunData);
    }

    setTestRunName(testRunId: TestRunId, name: string): Observable<GenericApiResponse> {
        return this.http.post<any>(`/testmgmt/testruns/${testRunId}/name`, { name });
    }

    deleteTestRun(testRunId: TestRunId): Observable<GenericApiResponse> {
        return this.http.delete<any>(`/testmgmt/testruns/${testRunId}`);
    }

    generateTestRunReport(testRunId: TestRunId, config: TestRunReportConfig): Observable<ReportId> {
        const normalizeReportId = (response: ReportId | {value?: ReportId}): ReportId => {
            if (typeof response === 'string' && response.trim().length > 0) {
                return response;
            }
            if (response && typeof response === 'object' && typeof response.value === 'string' && response.value.trim().length > 0) {
                return response.value;
            }
            throw new Error('Unexpected TestRun report response format');
        };

        return this.http.post<ReportId | {value?: ReportId}>(`/testmgmt/testruns/${testRunId}/report`, config).pipe(
            map(normalizeReportId)
        );
    }

    getTestExecution(id: TestExecutionId): Observable<TestExecutionSummary> {
        return this.http.get<TestExecutionSummary>(`/testmgmt/testexecutions/${id}`);
    }

    createAndStartTestExecution(
        projectId: ProjectId,
        templateOrId: {name: string} | {id: TestTemplateId},
        s: TestRunCreationSpec,
        displayType?: string,
        name?: string,
        tags?: Tag[],
        automationPolicy?: AutomationPolicyConfig
    ): Observable<CreateTestExecutionResult> {
        // TODO set remaining fields properly
        let req: any = {
            testTemplateIdOrName: templateOrId,
            tags: tags || [],
            models: [],
            associatedSessions: [],
            testRunCreationSpec: s
        };
        if (name && name.length > 0) req['executionName'] = name;
        if (displayType) req['displayType'] = displayType;
        if (automationPolicy) req['automationPolicy'] = automationPolicy;
        return this.http.post<CreateTestExecutionResult>(`/projects/${projectId}/testexecutions?start=true`, req);
    }

    createTestExecution(projectId: ProjectId, testId: TestTemplateId, s: TestRunCreationSpec): Observable<CreateTestExecutionResult> {
        return this.http.post<CreateTestExecutionResult>(`/projects/${projectId}/testexecutions`, s, {params: {testId: testId}});
    }

    startTestExecution(executionId: TestExecutionId): Observable<GenericApiResponse> {
        return this.http.post<GenericApiResponse>(`/testmgmt/testexecutions/${executionId}`, {} /* TODO scheduling config*/);
    }

    getTestExecutionsForTest(projectId: ProjectId, testId: TestTemplateId): ApiSourcedPaginationRequest<TestExecutionSummary> {
        return new ApiSourcedPaginationRequest<TestExecutionSummary>(`/projects/${projectId}/testexecutions`, this.http, {testId: testId});
    }

    getTestRun(testRunId: TestRunId): Observable<TestRun> {
        return this.http.get<TestRun>(`/testmgmt/testruns/${testRunId}`);
    }

    getTestRunResultProjection(testRunId: TestRunId): Observable<TestRunResultProjection> {
        return this.http.get<TestRunResultProjection>(`/testmgmt/testruns/${testRunId}/result-projection`);
    }

    getTestRunsOfTestExecution(testExecutionId: TestExecutionId): Observable<Array<TestRun>> {
        return this.http.get<TestRun[]>(`/testmgmt/testexecutions/${testExecutionId}/testruns`);
    }

    getFindingsForTestRun(testRunId: TestRunId, forceRefresh: boolean = false): ApiSourcedPaginationRequest<Finding> {
        return new ApiSourcedPaginationRequest<Finding>(`/testmgmt/testruns/${testRunId}/results?refresh=${forceRefresh}`, this.http, undefined);
    }

    getFindingInfosForAudit(auditId: AuditId, forceRefresh: boolean = false): Observable<FindingInfo[]> {
        return this.http.get<FindingInfo[]>(`/audit/audits/${auditId}/findinginfos?refresh=${forceRefresh}`);
    }

    getFinding$(id: FindingId): Observable<Finding> {
        return this.http.get<Finding>(`/testmgmt/findings/${id}`);
    }

    getFindings$(ids: FindingId[]): Observable<Finding[]> {
        // we don't want to get the request get too big, so we split the request into chunks of 20
        const chunkSize = 20;
        let observables: Observable<Finding[]>[] = [];
        for (let i = 0; i < ids.length; i += chunkSize) {
            const chunk = ids.slice(i, i + chunkSize);
            observables.push(this.http.get<Finding[]>(`/testmgmt/findings`, {params: {ids: chunk.join(',')}}).pipe(take(1)));
        }

        if (observables.length === 0) {
            return of([]);
        } else {
            return forkJoin(observables).pipe(map((chunks) => {
                return chunks.reduce((acc, chunk) => acc.concat(chunk), []);
            }));
        }
    }

    resolveTestObjects$(projectId: ProjectId, sourceSpec: TestObjectsSourceSpec): Observable<TestObjectResolutionJob> {
        return this.http.post<TestObjectResolutionJob>(`/projects/${projectId}/testmgmt/testobjects/resolve`, sourceSpec);
    }

    getTestObjectResolutionJob$(projectId: ProjectId, jobId: TestObjectResolutionJobId): Observable<TestObjectResolutionJob> {
        return this.http.get<TestObjectResolutionJob>(`/projects/${projectId}/testmgmt/testobjectresolutionjobs/${jobId}`);
    }

    captureRepresentativeState$(testObjectId: TestObjectId): Observable<StateCollectorJobId> {
        return this.http.post<StateCollectorJobId>(`/testmgmt/testobjects/${testObjectId}/representative-state-capture`, {});
    }

    openTestObjectInExpedition$(testObjectId: TestObjectId, request: OpenTestObjectInExpeditionRequest): Observable<void> {
        return this.http.post<void>(`/testmgmt/testobjects/${testObjectId}/open-in-expedition`, request);
    }

    deleteTestObject(testObjectId: TestObjectId): Observable<GenericApiResponse> {
        return this.http.delete<GenericApiResponse>(`/testmgmt/testobjects/${testObjectId}`);
    }

    getTestObjects$(ids: TestObjectId[]): Observable<TestObject[]> {
        // keep request sizes bounded for long id lists
        const chunkSize = 20;
        let observables: Observable<TestObject[]>[] = [];
        for (let i = 0; i < ids.length; i += chunkSize) {
            const chunk = ids.slice(i, i + chunkSize);
            observables.push(this.http.get<TestObject[]>(`/testmgmt/testobjects`, {params: {ids: chunk.join(',')}}).pipe(take(1)));
        }

        if (observables.length === 0) {
            return of([]);
        } else {
            return forkJoin(observables).pipe(map((chunks) => {
                return chunks.reduce((acc, chunk) => acc.concat(chunk), []);
            }));
        }
    }

    getTestObjectsInProject$(projectId: ProjectId, queryMetadata?: QueryMetadata): Observable<TestObject[]> {
        return this.http.get<TestObject[]>(`/projects/${projectId}/testmgmt/testobjects`);
    }

    createTestObject$(projectId: ProjectId, dto: CreateTestObjectDTO): Observable<TestObjectId> {
        return this.http.post<TestObjectId>(`/projects/${projectId}/testmgmt/testobjects`, dto);
    }

    getStatesForTestObject$(testObjectId: TestObjectId, queryMetadata?: QueryMetadata): Observable<APIResult<TestObjectStateSummary[]>> {
        return this.http.get<APIResult<TestObjectStateSummary[]>>(`/testmgmt/testobjects/${testObjectId}/states`, {
            params: queryMetadata ? queryMetadataToQueryParams(queryMetadata) : undefined
        });
    }

    getTestObjectCollectionsInProject$(projectId: ProjectId, includeDeleted: boolean = false): Observable<TestObjectCollectionSummary[]> {
        const params = {includeDeleted: includeDeleted.toString()};
        return this.http.get<TestObjectCollectionSummary[]>(`/projects/${projectId}/testmgmt/testobjectcollections`, {params});
    }

    getTestObjectCollection$(collectionId: TestObjectCollectionId, includeDeleted: boolean = false): Observable<TestObjectCollection> {
        const params = {includeDeleted: includeDeleted.toString()};
        return this.http.get<TestObjectCollection>(`/testmgmt/testobjectcollections/${collectionId}`, {params});
    }

    createTestObjectCollection$(projectId: ProjectId, dto: CreateTestObjectCollectionDTO): Observable<TestObjectCollection> {
        return this.http.post<TestObjectCollection>(`/projects/${projectId}/testmgmt/testobjectcollections`, dto);
    }

    updateTestObjectCollection$(collectionId: TestObjectCollectionId, updateActions: TestObjectCollectionUpdateAction[]): Observable<TestObjectCollection> {
        return this.http.post<TestObjectCollection>(`/testmgmt/testobjectcollections/${collectionId}`, updateActions);
    }

    deleteTestObjectCollection$(collectionId: TestObjectCollectionId): Observable<GenericApiResponse> {
        return this.http.delete<GenericApiResponse>(`/testmgmt/testobjectcollections/${collectionId}`);
    }

    queryFindings$(projectId: ProjectId, spec: FindingsQuerySpec): Observable<APIResult<FindingsQueryResult>> {
        return this.http.post<APIResult<FindingsQueryResult>>(`/projects/${projectId}/testmgmt/findings-query`, spec);
    }

    queryFindingsAsCSV$(projectId: ProjectId, spec: FindingsQuerySpec): Observable<Blob> {
        return this.http.post(`/projects/${projectId}/testmgmt/findings-query?view=csv&flatten=true`, spec, {responseType: 'blob'});
    }

    updateTestObject(testObjectId: TestObjectId, updateActions: TestObjectUpdateAction[]): Observable<GenericApiResponse> {
        return this.http.post<GenericApiResponse>(`/testmgmt/testobjects/${testObjectId}`, updateActions);
    }

    addManualFinding(testRunId: TestRunId, issueText: string, severity: IssueSeverity): Observable<FindingId> {
        const data: FindingUpdateData = {
            associatedArtifacts: [],
            issueData: {},
            severity: severity.toString(),
            findingType: 'Manual.AdHocResult'
        };
        return this.http.post<FindingId>(`/testmgmt/testruns/${testRunId}/results`, data);
    }

    createFinding(projectId: ProjectId, finding: FindingUpdateData): Observable<FindingId> {
        return this.http.post<FindingId>(`/projects/${projectId}/findings`, finding);
    }

    createFindingsBulk(projectId: ProjectId, findings: FindingCreationData[]): Observable<FindingId[]> {
        return this.http.post<{findingIds: FindingId[]}>(`/projects/${projectId}/findings/bulk`, {findings}).pipe(
            map(response => response.findingIds)
        );
    }

    updateFinding(findingId: FindingId, finding: FindingUpdateData): Observable<GenericApiResponse> {
        return this.http.post<GenericApiResponse>(`/testmgmt/findings/${findingId}`, finding);
    }

    updateFindingsBulk(projectId: ProjectId, findingIds: FindingId[], updateData: FindingUpdateData): Observable<{updated: number}> {
        return this.http.post<{updated: number}>(`/projects/${projectId}/testmgmt/findings/bulk`, {findingIds, updateData});
    }

    updateFindingsBulkByFilter(projectId: ProjectId, querySpec: FindingsQuerySpec, update: FindingUpdateData): Observable<{updated: number}> {
        return this.http.post<{updated: number}>(`/projects/${projectId}/testmgmt/findings/bulk-by-filter`, {querySpec, update});
    }

    /**
     * Perform an action on a finding cell
     * @param action the action to perform. See FindingCellAction for details
     */
    performFindindCellAction(findingId: FindingId, action: FindingCellAction): Observable<void> {
        return this.http.post<void>(`/testmgmt/findings/${findingId}/cells`, action);
    }

    getQASessionsInProject$(projectId: ProjectId): Observable<QASession[]> {
        return this.http.get<QASession[]>(`/projects/${projectId}/testmgmt/qasessions`);
    }

    createQASession$(projectId: ProjectId, data: CreateQASessionData): Observable<QASession> {
        return this.http.post<QASession>(`/projects/${projectId}/testmgmt/qasessions`, data);
    }

    getQASession$(projectId: ProjectId, sessionId: QASessionId): Observable<QASession> {
        return this.http.get<QASession>(`/projects/${projectId}/testmgmt/qasessions/${sessionId}`);
    }

    updateQASession$(projectId: ProjectId, sessionId: QASessionId, data: UpdateQASessionData): Observable<QASession> {
        return this.http.put<QASession>(`/projects/${projectId}/testmgmt/qasessions/${sessionId}`, data);
    }

    closeQASession$(projectId: ProjectId, sessionId: QASessionId): Observable<QASession> {
        return this.http.post<QASession>(`/projects/${projectId}/testmgmt/qasessions/${sessionId}/close`, {});
    }

    archiveQASession$(projectId: ProjectId, sessionId: QASessionId): Observable<QASession> {
        return this.http.post<QASession>(`/projects/${projectId}/testmgmt/qasessions/${sessionId}/archive`, {});
    }

    getQASessionAssociations$(projectId: ProjectId, sessionId: QASessionId): Observable<QASessionAssociation[]> {
        return this.http.get<QASessionAssociation[]>(`/projects/${projectId}/testmgmt/qasessions/${sessionId}/associations`);
    }

    addQASessionAssociation$(projectId: ProjectId, sessionId: QASessionId, data: CreateQASessionAssociationData): Observable<QASessionAssociation> {
        return this.http.post<QASessionAssociation>(`/projects/${projectId}/testmgmt/qasessions/${sessionId}/associations`, data);
    }

    removeQASessionAssociation$(projectId: ProjectId, sessionId: QASessionId, associationId: QASessionAssociationId): Observable<GenericApiResponse> {
        return this.http.delete<GenericApiResponse>(`/projects/${projectId}/testmgmt/qasessions/${sessionId}/associations/${associationId}`);
    }

    getQASessionsByTarget$(projectId: ProjectId, targetType: QASessionAssociationTargetType, targetId: string): Observable<QASession[]> {
        return this.http.get<QASession[]>(`/projects/${projectId}/testmgmt/qasessions/by-target`, {
            params: {
                targetType: targetType,
                targetId: targetId
            }
        });
    }

    // TODO everything below

    /**
     * Get TestSessionSummaries for all TestSessios in the given project
     */
    getTestSessionsInProject$(projectId: ProjectId, queryMetadata?: QueryMetadata): ApiSourcedPaginationRequest<TestSessionInfo> {
        return new ApiSourcedPaginationRequest<TestSessionInfo>(`/projects/${projectId}/testsessions`, this.http, !!queryMetadata ? {...queryMetadata.filters} : {});
    }

    /**
     * Get TestSessionSummaries for all TestSessios in the given project
     */
    createTestSession(projectId: ProjectId, name: string): Observable<TestSessionId> {
        let sessionInfo = {
            name: name
        };

        return this.http.post<TestSessionId>(`/projects/${projectId}/testsessions`, sessionInfo);
    }

    getTestSession$(testSessionId: TestSessionId): Observable<TestSession> {
        return this.http.get<TestSession>(`/testmgmt/testsessions/${testSessionId}`);
    }

    setTagRule(tagRule: TagRule, projectId: ProjectId): Observable<FindingTagRuleId> {
        return this.http.post<any>(`/projects/${projectId}/finding-tagrules`, tagRule)
    }

    storeTestReportSpec(projectId: ProjectId, name: string, filterSpec: FilterSpec, groupingSpec: GroupingSpec, aggregationSpec: GroupingSpec, shared: boolean): Observable<TestReportSpec> {
        let creationDto = {
            name: name,
            filterSpec: filterSpec,
            groupingSpec: groupingSpec,
            aggregationSpec: aggregationSpec,
            shared: shared
        };
        return this.http.post<TestReportSpec>(`/projects/${projectId}/testmgmt/reportspecs`, creationDto);
    }

    deleteTestReportSpecs(projectId: ProjectId, id: ReportSpecId): Observable<any> {
        return this.http.delete<any>(`/projects/${projectId}/testmgmt/reportspecs/${id}`);
    }

    getTestReportSpecs$(projectId: ProjectId): Observable<Array<TestReportSpec>> {
        return this.http.get<TestReportSpec[]>(`/projects/${projectId}/testmgmt/reportspecs`);
    }

    queryAdhocTestReports$(projectId: ProjectId, adhocReportSpec: AdHocReportSpec): Observable<any> {
        return this.http.post<any>(`/projects/${projectId}/testmgmt/report`, adhocReportSpec);
    }

    exportAdhocTestReportAsCsv$(projectId: ProjectId, adhocReportSpec: AdHocReportSpec): Observable<Blob> {
        return this.http.post(`/projects/${projectId}/testmgmt/report?view=csv`, adhocReportSpec, {responseType: 'blob'});
    }

    getIssueReportersForCustomer(): Observable<IssueReporterIntegration[]> {
        return this.http.get<IssueReporterIntegration[]>(`/testmgmt/issuereporters`);
    }

    getSpecs$(specIds: SpecId[]): Observable<Spec[]> {
        // we don't want to get the request get too big, so we split the request into chunks of 20
        const chunkSize = 20;
        let observables: Observable<Spec[]>[] = [];
        for (let i = 0; i < specIds.length; i += chunkSize) {
            const chunk = specIds.slice(i, i + chunkSize);
            observables.push(this.http.get<Spec[]>(`/testmgmt/specs`, {params: {ids: chunk.join(',')}}).pipe(take(1)));
        }

        if (observables.length === 0) {
            return of([]);
        } else {
            return forkJoin(observables).pipe(map((chunks) => {
                return chunks.reduce((acc, chunk) => acc.concat(chunk), []);
            }));
        }
    }

    resolveSpecReferences$(specRefs: SpecRef[]): Observable<Spec[]> {
        // we don't want to get the request get too big, so we split the request into chunks of 20
        const chunkSize = 20;
        let observables: Observable<Spec[]>[] = [];
        for (let i = 0; i < specRefs.length; i += chunkSize) {
            const chunk = specRefs.slice(i, i + chunkSize);
            observables.push(this.http.post<Spec[]>(`/testmgmt/specrefs`, {params: {ids: chunk.join(',')}}).pipe(take(1)));
        }

        return forkJoin(observables).pipe(map((chunks) => {
            return chunks.reduce((acc, chunk) => acc.concat(chunk), []);
        }));
    }

    getSpecsInProject$(projectId: ProjectId): Observable<Spec[]> {
        return this.http.get<Spec[]>(`/projects/${projectId}/testmgmt/specs`);
    }

    getSpecCollectionsInProject$(projectId: ProjectId): Observable<SpecCollectionDTO[]> {
        const listParams = {includeDeleted: 'false'};
        const listIds$ = this.http.get<string[]>(`/projects/${projectId}/testmgmt/speccollections`, {params: listParams});

        const getByRef = (collectionRef: string) => {
            const getParams = {includeDeleted: 'false'};
            return this.http.get<SpecCollectionDTO>(`/testmgmt/speccollections/${collectionRef}`, {params: getParams});
        };

        return listIds$.pipe(
            switchMap((ids: string[]) => ids.length ? forkJoin(ids.map(id => getByRef(id))) : of<SpecCollectionDTO[]>([]))
        );
    }

    getSpecForestInProject$(projectId: ProjectId, optRoot?: SpecGroupId, maxLevel?: number, includeSpecs?: boolean): Observable<BackendSpecForestNode[]> {
        const params: any = {};
        if (optRoot) {
            params['root'] = optRoot;
            params['rootGroup'] = optRoot;
        }
        if (maxLevel !== undefined) {
            params['maxLevel'] = maxLevel.toString();
        }
        if (includeSpecs !== undefined) {
            params['includeSpecs'] = includeSpecs;
        }

        return this.http.get<BackendSpecForestNode[]>(`/projects/${projectId}/testmgmt/specforest`, {params});
    }

    getSpecGroups$(ids: SpecGroupId[]): Observable<SpecGroup[]> {
        // we don't want to get the request get too big, so we split the request into chunks of 20
        const chunkSize = 20;
        let observables: Observable<SpecGroup[]>[] = [];
        for (let i = 0; i < ids.length; i += chunkSize) {
            const chunk = ids.slice(i, i + chunkSize);
            observables.push(this.http.get<SpecGroup[]>(`/testmgmt/specgroups`, {params: {ids: chunk.join(',')}}).pipe(take(1)));
        }

        return forkJoin(observables).pipe(map((chunks) => {
            return chunks.reduce((acc, chunk) => acc.concat(chunk), []);
        }));
    }

    updateSpec(id: SpecId, updateActions: SpecUpdateAction[]): Observable<GenericApiResponse> {
        return this.http.post<GenericApiResponse>(`/testmgmt/specs/${id}`, updateActions);
    }

    createSpec(projectId: ProjectId, spec: CreateSpecDTO): Observable<SpecId> {
        return this.http.post<SpecId>(`/projects/${projectId}/testmgmt/specs`, spec);
    }

    deleteSpec(id: SpecId): Observable<GenericApiResponse> {
        return this.http.delete<GenericApiResponse>(`/testmgmt/specs/${id}`);
    }

    createSpecGroup(projectId: ProjectId, specGroup: CreateSpecGroupDTO): Observable<SpecGroupId> {
        return this.http.post<SpecGroupId>(`/projects/${projectId}/testmgmt/specgroups`, specGroup);
    }

    deleteSpecGroup(id: SpecGroupId): Observable<GenericApiResponse> {
        return this.http.delete<GenericApiResponse>(`/testmgmt/specgroups/${id}`);
    }

    updateSpecGroup(id: SpecGroupId, updateActions: SpecGroupUpdateAction[]): Observable<GenericApiResponse> {
        return this.http.post<GenericApiResponse>(`/testmgmt/specgroups/${id}`, updateActions);
    }

    /// Issue Reporter API
    getIssueReporterProjectsWithIssueTypes(issueReporterId: IntegrationId): Observable<IssueReporterProjectWithIssueTypes[]> {
        return this.http.get<IssueReporterProjectWithIssueTypes[]>(`/testmgmt/issuereporters/${issueReporterId}/projects`);
    }

    getIssueReporterParams(issueReporterId: IntegrationId, issueReporterProjectId?: string, issueTypeId?: string): Observable<IssueReporterParametersResponse> {
        const params: any = {};
        if (issueReporterProjectId) {
            params.issueReporterProjectId = issueReporterProjectId;
        }
        if (issueTypeId) {
            params.issueTypeId = issueTypeId;
        }

        return this.http.get<IssueReporterParametersResponse>(`/testmgmt/issuereporters/${issueReporterId}/params`, {params});
    }

    getIssueReporterDefaults(issueReporterId: IntegrationId, projectId: ProjectId, issueSourceType: IssueSourceType, issueSourceId: string): Observable<IssueReporterDefaults> {
        return this.http.get<IssueReporterDefaults>(`/testmgmt/issuereporters/${issueReporterId}/defaults`, {params: {projectId, issueSourceType, issueSourceId}});
    }

    addIssueReporterConfig(issueReporterConfig: IssueReporterInsertDTO): Observable<IntegrationId> {
        return this.http.post<IntegrationId>(`/testmgmt/issuereporters`, issueReporterConfig);
    }

    submitIssueReport(id: IntegrationId, params: {[s: string]: WMValue<any>}, attachments: ObservationData[], projectId: ProjectId): Observable<IssueReportingInfo> {
        const submitBody = { params, attachments, projectId };
        return this.http.post<IssueReportingInfo>(`/testmgmt/issuereporters/${id}/reports`, submitBody);
    }

    updateIssueReporterConfig(issueReporterConfig: IssueReporterIntegration): Observable<GenericApiResponse> {
        return this.http.post<GenericApiResponse>(`/testmgmt/issuereporters/${issueReporterConfig.id}`, issueReporterConfig);
    }

    deleteIssueReporterConfig(issueReporterConfigId: string): Observable<GenericApiResponse> {
        return this.http.delete<GenericApiResponse>(`/testmgmt/issuereporters/${issueReporterConfigId}`);
    }

    createFindingReport(projectId: ProjectId, findings: FindingId[], language?: string): Observable<ReportId> {
        return this.http.post<ReportId>(`/projects/${projectId}/testmgmt/findings-report`, { findings, renderConfig: { language: language || "en" } });
    }
}
